import { EDITOR_VERSION, MP_FEATURE_SESSION_STORAGE } from 'constants/mixpanel';

/**
 * Get item from session storage given key
 * returns parsed JSON object
 *
 * @param key - key to fetch
 * @param parseJSON - if TRUE will use JSON.parse() to parse session storage item, will return the parsed object
 * @returns object - parsed session storage item
 */
const getSessionStorageItem = (key: string, parseJSON = true) => {
  if (!window.sessionStorage) return null;

  let obj: object | null = null;
  try {
    const item = window.sessionStorage.getItem(key);
    if (item) {
      obj = parseJSON ? JSON.parse(item) : item;
    }
  } catch (err: any) {
    console.error(`Unable to fetch local storage item: ${key}`, err.message);
  }

  return obj;
};

/**
 * Set item in session storage
 * Overwrites the specified key
 *
 * @param key: string key to use
 * @param obj: object to store
 * @returns boolean - TRUE if session storage item successfully set
 */
const setSessionStorageItem = (key: string, obj: object) => {
  if (!window.sessionStorage) return false;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(obj));
  } catch (err: any) {
    console.error(`Unable to set session storage item: ${key}`, err.message);
    return false;
  }

  return true;
};

/**
 * Helper func to set mixpanel_feature session storage item used by `Analytics: Upgrade` event
 *
 * @param key: string key to use
 * @param obj: object to store
 * @returns boolean - TRUE if session storage item successfully set
 */
const setAnalyticsUpgradeFeatureProp = (featureName: string, widgetName?: string) => {
  setSessionStorageItem(MP_FEATURE_SESSION_STORAGE, {
    data: {
      Feature: featureName,
      widget_type: widgetName,
      editor_version: EDITOR_VERSION,
    },
  });
};

/**
 * Removes item from session storage
 *
 * @param key - item to remove
 * @returns boolean - TRUE if item was successfully removed
 */
const clearSessionStorageItem = (key: string) => {
  if (!window.sessionStorage) return false;

  try {
    window.sessionStorage.removeItem(key);
  } catch (err: any) {
    console.error(`Unable to clear session storage item: ${key}`, err.message);
    return false;
  }

  return true;
};

export { getSessionStorageItem, setSessionStorageItem, clearSessionStorageItem, setAnalyticsUpgradeFeatureProp };
